import React, { useEffect, useState } from 'react';

import { Box, Button, Divider, Group, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconPinned, IconPinnedFilled, IconPlus, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useNotes from 'hooks/notes/useNotes';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteRetrieve, NoteList } from 'types';

import DrawerNoteForm from '../../Notes/components/DrawerNoteForm';
import NoteDeleteModal from '../../Notes/components/NoteDeleteModal';

const AthleteNotesContent = ({
  athlete,
  permission = true,
}: {
  athlete: AthleteRetrieve;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { dateTimeFormat } = useLocalization();

  const [noteId, setNoteId] = useState<string | null>(null);
  const [isCreateDrawerOpened, { toggle: handleCreateDrawerToggle }] = useDisclosure(false);
  const [isDeleteModalOpened, { toggle: handleDeleteModalToggle }] = useDisclosure(false);

  const { data, isSuccess, isError, isLoading } = useNotes({
    athleteId: athlete?.athleteId,
    ordering: '-createdAt',
    limit: '4',
  });
  const [tableData, setTableData] = useState<NoteList[]>([]);

  const handleNavigate = (): void => {
    navigate(`/users/athletes/${athlete.athleteId}/notes`);
  };

  const handleDelete = (note: NoteList): void => {
    setNoteId(note.noteId);
    handleDeleteModalToggle();
  };

  const handleCreateNote = (): void => {
    setNoteId(null);
    handleCreateDrawerToggle();
  };

  const handleViewNote = (id: string): void => {
    setNoteId(id);
    handleCreateDrawerToggle();
  };

  const columns = [
    {
      Header: 'Date & Time',
      accessor: 'createdAt',
      Cell: ({ value }: CellProps<NoteList>): React.ReactNode => dayjs(value).format(dateTimeFormat),
    },
    {
      Header: 'Title',
      accessor: 'title',
      Cell: ({ row }: CellProps<NoteList>): React.ReactNode => (
        <Text lineClamp={3}>{row.original.title}</Text>
      ),
    },
    {
      Header: 'Note',
      accessor: 'content',
      Cell: ({ row }: CellProps<NoteList>) => (
        <Text
          sx={{
            wordBreak: 'break-word',
            whiteSpace: 'break-spaces',
          }}
          lineClamp={3}
          dangerouslySetInnerHTML={{ __html: row.original.content }}
        />
      ),
    },
    {
      Header: 'Pin',
      accessor: 'pinned',
      Cell: ({ row }: CellProps<NoteList>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {row.original.pinned ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.colors.green[7] }}>
              <IconPinnedFilled size={20} /> On
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.colors.gray[7] }}>
              <IconPinned size={20} /> Off
            </Box>
          )}
        </Box>
      ),
    },
    {
      Header: 'Created By',
      accessor: 'createdBy.fullName',
    },
    {
      id: 'actions',
      disableSortBy: true,
      Cell: ({ row }: CellProps<NoteList>) => {
        return (
          <Group position='right'>
            <Menu
              disabled={!permission}
              items={[
                {
                  title: 'View',
                  icon: <IconEdit size={theme.fontSizes.sm} />,
                  onClick: () => handleViewNote?.(row.original.noteId),
                },
                {
                  title: 'Delete',
                  icon: <IconTrashX size={theme.fontSizes.sm} />,
                  color: 'red',
                  onClick: () => handleDelete(row.original),
                },
              ]}
            />
          </Group>
        );
      },
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results.slice(0, 3)).flat());
    }
  }, [isLoading, data]);

  return (
    <Paper shadow='xl' radius='md' withBorder py='sm' px={0}>
      <Stack spacing={0}>
        <Group position='apart' px='md' pb='sm'>
          <Title order={4}>Notes</Title>

          <Button
            compact
            leftIcon={<IconPlus size={theme.fontSizes.md} />}
            disabled={!permission}
            onClick={handleCreateNote}
          >
            Note
          </Button>
        </Group>
        <Divider />

        <Table
          tableData={tableData}
          columns={columns}
          isSuccess={isSuccess}
          isError={isError}
          isLoading={isLoading}
          margin={8}
        />
        {isSuccess && data?.pages[0].data.count > 3 && (
          <Button compact onClick={handleNavigate} variant='transparent'>
            See More Notes
          </Button>
        )}
      </Stack>
      <DrawerNoteForm
        athleteId={athlete.athleteId}
        opened={isCreateDrawerOpened}
        closeHandler={handleCreateDrawerToggle}
        noteId={noteId}
        setNoteId={setNoteId}
        permission={permission}
      />
      <NoteDeleteModal
        opened={isDeleteModalOpened}
        noteId={noteId || ''}
        toggleDrawer={handleDeleteModalToggle}
      />
    </Paper>
  );
};

export default AthleteNotesContent;
